import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createNewsletter,
  updateNewsletter,
} from "../redux/actions/newsletterActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getTerms } from "../redux/actions/termActions";
import { getBranches } from "../redux/actions/branchActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { newsletterActions } from "../redux/slices/newsletter";
import { cleanFormData } from "../helpers/formHelpers";

import { NewsletterAudience } from "../shared/enums/NewsletterAudience";
import { NewsletterResponseDto } from "../shared/dtos/newsletter/NewsletterResponseDto";
import { NewsletterRequestDto } from "../shared/dtos/newsletter/NewsletterRequestDto";

const audienceOptions = Object.values(NewsletterAudience).map((aud) => ({
  value: aud,
  label: aud.replace(/_/g, " "),
}));

const newsletterSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  bodyContent: Yup.string().required("Content is required"),
  publicationDate: Yup.string().required("Publication date is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
  termId: Yup.string().nullable(),
  branchId: Yup.string().nullable(),
  gradeLevelId: Yup.string().nullable(),
  audienceScope: Yup.mixed<NewsletterAudience>()
    .oneOf(Object.values(NewsletterAudience))
    .required("Audience scope is required"),
});

interface CreateOrUpdateNewsletterFormProps {
  newsletter?: NewsletterResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateNewsletterForm: React.FC<
  CreateOrUpdateNewsletterFormProps
> = ({
  newsletter = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: terms } = useAppSelector((state) => state.term);
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { userInfo } = useAppSelector((state) => state.auth);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (!academicYears || academicYears.length === 0)
      dispatch(getAcademicYears());
    if (!terms || terms.length === 0) dispatch(getTerms());
    if (isSuperAdmin && (!branches || branches.length === 0))
      dispatch(getBranches());
    if (!gradeLevels || gradeLevels.length === 0) dispatch(getGradeLevels());
  }, [dispatch, academicYears, terms, branches, gradeLevels, isSuperAdmin]);

  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const termOptions = useMemo(() => {
    const list = terms?.map((t) => ({ value: t.id, label: t.name })) || [];
    return [{ value: "", label: "General (No Specific Term)" }, ...list];
  }, [terms]);

  const branchOptions = useMemo(() => {
    const list = branches?.map((b) => ({ value: b.id, label: b.name })) || [];
    return [{ value: "", label: "All Campuses (General)" }, ...list];
  }, [branches]);

  const gradeOptions = useMemo(() => {
    const list =
      gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [];
    return [{ value: "", label: "All Grade Levels" }, ...list];
  }, [gradeLevels]);

  const initialValues: NewsletterRequestDto = {
    title: newsletter?.title || "",
    bodyContent: newsletter?.bodyContent || "",
    publicationDate:
      newsletter?.publicationDate?.substring(0, 10) ||
      new Date().toISOString().split("T")[0],
    academicYearId: "",
    termId: "",
    branchId: isSuperAdmin ? "" : userInfo?.branchId || "",
    gradeLevelId: "",
    audienceScope:
      (newsletter?.audienceScope as NewsletterAudience) ||
      NewsletterAudience.SCHOOL_WIDE,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(newsletterActions.setErrorUpdate(null))
            : () => dispatch(newsletterActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={newsletterSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: NewsletterRequestDto = {
            title: rawCleaned.title,
            bodyContent: rawCleaned.bodyContent,
            publicationDate: rawCleaned.publicationDate,
            academicYearId: rawCleaned.academicYearId,
            termId: rawCleaned.termId || null,
            branchId: rawCleaned.branchId || null,
            gradeLevelId: rawCleaned.gradeLevelId || null,
            audienceScope: values.audienceScope || null,
          };

          if (isEditing && newsletter) {
            dispatch(
              updateNewsletter({ id: newsletter.id, values: cleanedValues }),
            );
          } else {
            dispatch(createNewsletter(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<NewsletterRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <FormTextField
                name="title"
                label="Newsletter Title (e.g., Term 1 Closing Circular)"
              />

              <FormTextField
                name="bodyContent"
                label="Message Content"
                multiline
                rows={6}
                placeholder="Type the principal's message or circular contents here..."
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="publicationDate"
                  label="Publication Date"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormSelectField
                  name="audienceScope"
                  label="Target Audience Scope"
                  options={audienceOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="academicYearId"
                  label="Academic Year"
                  options={yearOptions}
                />
                <FormSelectField
                  name="termId"
                  label="Term (Optional)"
                  options={termOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {isSuperAdmin && (
                  <FormSelectField
                    name="branchId"
                    label="Campus / Branch (Optional)"
                    options={branchOptions}
                  />
                )}
                <FormSelectField
                  name="gradeLevelId"
                  label="Grade Level (Optional)"
                  options={gradeOptions}
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Publish Newsletter"}
                loadingText={isEditing ? "Saving..." : "Publishing..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateNewsletterForm;
